import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { PMDialog } from "../../utils/PMDialog";
import ModernSupplierSelector from "../../utils/Selector/ModernSupplierSelector";
import { supplierProductsApi } from "../../../api/inventory/supplier-products";

export function SupplierProductsCard({ product }) {
  const { t } = useTranslation();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const roles = useSelector((state) => state.session.currentUser?.roles || []);
  const canViewCost = roles.some((role) => role.name === "admin" || role.name === "inventory_finance");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState(null);
  const [supplierOpen, setSupplierOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows([]);
    setForm(null);
    setSupplierOpen(false);
    if (!product?.id || !companyId) return undefined;
    let active = true;
    setLoading(true);
    setUnavailable(false);
    supplierProductsApi.list(product.id).then((response) => {
      if (active) setRows(response.data.data || []);
    }).catch((failure) => {
      if (!active) return;
      if ([401, 403].includes(failure?.response?.status)) setUnavailable(true);
      else setError("supplierProducts.loadFailed");
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [product?.id, companyId]);

  if (!product?.id || unavailable) return null;

  const startCreate = () => {
    setError(null);
    setForm({ supplier: null, item_number: product.item_number || "", title: product.title || "",
      purchase_price: product.purchase_price ?? "" });
  };
  const save = async () => {
    if (!form?.supplier?.id || !form.item_number.trim() || !form.title.trim() || saving) return;
    setSaving(true);
    setError(null);
    try {
      const payload = { supplier_id: form.supplier.id, product_id: product.id,
        item_number: form.item_number.trim(), title: form.title.trim(),
        purchase_price: form.purchase_price === "" ? null : form.purchase_price };
      const response = form.id
        ? await supplierProductsApi.update(form.id, payload)
        : await supplierProductsApi.create(payload);
      const saved = response.data.data;
      setRows((current) => [...current.filter((row) => row.id !== saved.id), saved]
        .sort((a, b) => a.supplier_name.localeCompare(b.supplier_name)));
      setForm(null);
    } catch (failure) {
      setError(failure?.response?.data?.errors?.join(", ") || "supplierProducts.saveFailed");
    } finally { setSaving(false); }
  };

  return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-5 py-4">
      <div><h2 className="text-base font-semibold text-slate-900">{t("supplierProducts.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("supplierProducts.description")}</p></div>
      {canViewCost && <button type="button" onClick={startCreate} className="rounded-xl bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B3A78]">
        {t("supplierProducts.add")}</button>}
    </div>
    {error && <p role="alert" className="m-4 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900">{t(error)}</p>}
    {form && <div className="grid gap-3 border-b border-slate-200 bg-slate-50 p-4 sm:grid-cols-2">
      <div className="sm:col-span-2"><label className="mb-1 block text-xs font-semibold text-slate-600">{t("supplierProducts.supplier")}</label>
        <button type="button" onClick={() => setSupplierOpen(true)} disabled={!!form.id} className="min-h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-left text-sm disabled:bg-slate-100">
          {form.supplier?.name || form.supplier?.displayTitle || t("supplierProducts.chooseSupplier")}</button></div>
      <label className="text-xs font-semibold text-slate-600">{t("supplierProducts.itemNumber")}
        <input value={form.item_number} onChange={(event) => setForm({ ...form, item_number: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></label>
      <label className="text-xs font-semibold text-slate-600">{t("supplierProducts.productTitle")}
        <input value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></label>
      <label className="text-xs font-semibold text-slate-600">{t("supplierProducts.purchasePrice")}
        <input type="number" min="0" step="0.000001" value={form.purchase_price} onChange={(event) => setForm({ ...form, purchase_price: event.target.value })} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" /></label>
      <div className="flex items-end justify-end gap-2"><button type="button" onClick={() => setForm(null)} disabled={saving} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">{t("Cancel")}</button>
        <button type="button" onClick={save} disabled={saving || !form.supplier?.id || !form.item_number.trim() || !form.title.trim()} className="rounded-lg bg-[#1463C7] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{t("Save")}</button></div>
    </div>}
    <div className="overflow-x-auto"><table className="w-full min-w-[540px] text-left text-sm"><thead className="bg-slate-50 text-xs font-semibold text-slate-500"><tr>
      <th className="px-5 py-3">{t("supplierProducts.supplier")}</th><th className="px-5 py-3">{t("supplierProducts.itemNumber")}</th>
      <th className="px-5 py-3">{t("supplierProducts.productTitle")}</th>{canViewCost && <th className="px-5 py-3 text-right">{t("supplierProducts.purchasePrice")}</th>}
      {canViewCost && <th className="px-5 py-3" />}</tr></thead><tbody className="divide-y divide-slate-100">
      {rows.map((row) => <tr key={row.id}><td className="px-5 py-3 font-medium text-slate-800">{row.supplier_name}</td><td className="px-5 py-3 tabular-nums">{row.item_number}</td>
        <td className="px-5 py-3">{row.title}</td>{canViewCost && <td className="px-5 py-3 text-right tabular-nums">{row.purchase_price ?? "—"}</td>}
        {canViewCost && <td className="px-5 py-3 text-right"><button type="button" onClick={() => setForm({ ...row, supplier: { id: row.supplier_id, name: row.supplier_name } })} className="font-semibold text-[#1463C7] hover:underline">{t("Edit")}</button></td>}</tr>)}
    </tbody></table></div>
    {!loading && rows.length === 0 && <p className="px-5 py-7 text-center text-sm text-slate-500">{t("supplierProducts.empty")}</p>}
    {loading && <p className="px-5 py-4 text-sm text-slate-500">{t("Loading...")}</p>}
    <PMDialog open={supplierOpen} onClose={() => setSupplierOpen(false)} size="md" dense zIndex={2400}>
      <div className="h-[70vh]"><ModernSupplierSelector open={supplierOpen} selectedSuppliers={form?.supplier ? [form.supplier] : []}
        onChange={(suppliers) => setForm((current) => current ? { ...current, supplier: suppliers?.[0] || null } : current)}
        onClose={() => setSupplierOpen(false)} /></div>
    </PMDialog>
  </section>;
}
